import { useEffect, useState, useRef } from "react";
import { Stack, Group, Button, Select, MultiSelect, Checkbox, Text, Alert, Paper, ScrollArea, Container, Divider, Title, ThemeIcon, useMantineTheme, TextInput, Textarea, Space, SimpleGrid, NumberInput } from "@mantine/core";
import { DatePickerInput, TimeInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { useMediaQuery } from "@mantine/hooks";
import { IconAlertTriangle, IconCheck, IconChevronLeft, IconTrash } from "@tabler/icons-react";
import moment from "moment-timezone";

import {
	RecurrenceType,
	CreateWorkOrderScheduleDto,
	UpdateWorkOrderScheduleDto,
	RecurrencePatternDto,
	DaysOfWeek,
	MonthlyWeekOptions,
	WorkOrderScheduleDto,
} from "../../models/workorderschedule.models";
import { useGetWorkOrderTemplatesQuery } from "../../api/workordertemplate.api";
import classes from './ScheduleEditView.module.css';

interface ScheduleEditViewProps {
	schedule?: WorkOrderScheduleDto | null;
	isLoading: boolean;
	onSubmit: (data: CreateWorkOrderScheduleDto | UpdateWorkOrderScheduleDto) => void;
	onCancel: () => void;
	isSaving: boolean;
	onChanged?: (changed: boolean) => void;
}

export const ScheduleEditView = ({ schedule, isLoading, onSubmit, onCancel, isSaving, onChanged }: ScheduleEditViewProps) => {
	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`, true);
	const [recurrenceType, setRecurrenceType] = useState<RecurrenceType | null>(schedule?.RecurrenceType ?? RecurrenceType.Daily);
	const [errors, setErrors] = useState<string[]>([]);
	const prevDirty = useRef(false);

	const isNew = !schedule || schedule.ScheduleId === "new";

	const { data: templates } = useGetWorkOrderTemplatesQuery();

	// Helper functions to convert between UTC and local time using moment-timezone
	const utcToLocalTime = (utcTimeString: string): string => {
		if (!utcTimeString) return "13:00:00";
		return moment.utc(utcTimeString, "HH:mm:ss").local().format("HH:mm:ss");
	};

	const localToUtcTime = (localTimeString: string): string => {
		if (!localTimeString) return "13:00:00";
		return moment(localTimeString, "HH:mm:ss").utc().format("HH:mm:ss");
	};

	const form = useForm({
		initialValues: {
			ScheduleName: schedule?.ScheduleName ?? "",
			Description: schedule?.Description ?? "",
			WorkOrderTemplateId: schedule?.WorkOrderTemplateId ?? "",
			RecurrenceType: schedule?.RecurrenceType?.toString() ?? RecurrenceType.Daily.toString(),
			WeekInterval: schedule?.RecurrencePattern?.WeekInterval?.toString() ?? "1",
			DaysOfWeek: schedule?.RecurrencePattern?.DaysOfWeek?.map(d => d.toString()) ?? [],
			DayOfMonth: schedule?.RecurrencePattern?.DayOfMonth?.toString() ?? "",
			WeekOfMonth: schedule?.RecurrencePattern?.WeekOfMonth?.toString() ?? "",
			DayOfWeekForMonthly: schedule?.RecurrencePattern?.DayOfWeekForMonthly?.toString() ?? "",
			ExcludedDaysOfWeek: schedule?.ExcludedDaysOfWeek?.map(d => d.toString()) ?? [],
			StartDate: schedule?.StartDate ? new Date(schedule.StartDate) : new Date(),
			EndDate: schedule?.EndDate ? new Date(schedule.EndDate) : null,
			TimeOfExecution: utcToLocalTime(schedule?.TimeOfExecution ?? "13:00:00"),
			IsActive: schedule?.IsActive ?? true,
		},
		validate: {
			ScheduleName: (value) => (!value ? "Schedule name is required" : null),
			WorkOrderTemplateId: (value) => (!value ? "Work Order Template is required" : null),
			StartDate: (value) => (!value ? "Start date is required" : null),
		},
	});

	// Track dirty state and call onChanged
	useEffect(() => {
		if (onChanged && prevDirty.current !== form.isDirty()) {
			onChanged(form.isDirty());
			prevDirty.current = form.isDirty();
		}
	}, [form.isDirty(), onChanged]);

	useEffect(() => {
		if (form.values.RecurrenceType) {
			setRecurrenceType(parseInt(form.values.RecurrenceType) as RecurrenceType);
		}
	}, [form.values.RecurrenceType]);

	useEffect(() => {
		if (recurrenceType === RecurrenceType.OneTime) {
			form.setValues({
				...form.values,
				WeekInterval: "1",
				DaysOfWeek: [],
				DayOfMonth: "",
				WeekOfMonth: "",
				DayOfWeekForMonthly: "",
				ExcludedDaysOfWeek: [],
				EndDate: null,
			});
		}
	}, [recurrenceType]);

	useEffect(() => {
		form.setValues({
			ScheduleName: schedule?.ScheduleName ?? "",
			Description: schedule?.Description ?? "",
			WorkOrderTemplateId: schedule?.WorkOrderTemplateId ?? "",
			RecurrenceType: schedule?.RecurrenceType?.toString() ?? RecurrenceType.Daily.toString(),
			WeekInterval: schedule?.RecurrencePattern?.WeekInterval?.toString() ?? "1",
			DaysOfWeek: schedule?.RecurrencePattern?.DaysOfWeek?.map(d => d.toString()) ?? [],
			DayOfMonth: schedule?.RecurrencePattern?.DayOfMonth?.toString() ?? "",
			WeekOfMonth: schedule?.RecurrencePattern?.WeekOfMonth?.toString() ?? "",
			DayOfWeekForMonthly: schedule?.RecurrencePattern?.DayOfWeekForMonthly?.toString() ?? "",
			ExcludedDaysOfWeek: schedule?.ExcludedDaysOfWeek?.map(d => d.toString()) ?? [],
			StartDate: schedule?.StartDate ? new Date(schedule.StartDate) : new Date(),
			EndDate: schedule?.EndDate ? new Date(schedule.EndDate) : null,
			TimeOfExecution: utcToLocalTime(schedule?.TimeOfExecution ?? "13:00:00"),
			IsActive: schedule?.IsActive ?? true,
		})
	}, [schedule]);

	const validateAndSubmit = () => {
		form.validate();
		
		const newErrors: string[] = [];

		// Validate dates
		if (form.values.EndDate && new Date(form.values.EndDate) <= new Date(form.values.StartDate)) {
			newErrors.push("End date must be after start date");
		}

		// Validate recurrence pattern based on type
		if (recurrenceType === RecurrenceType.Weekly && form.values.DaysOfWeek.length === 0) {
			// newErrors.push("Please select at least one day of week for weekly recurrence");
			form.setErrors((prev) => ({ ...prev, DaysOfWeek: "Please select at least one day of week for weekly recurrence" }));
		}

		if (recurrenceType === RecurrenceType.Monthly) {
			const hasDayOfMonth = form.values.DayOfMonth && form.values.DayOfMonth !== "";
			const hasNthDay = form.values.WeekOfMonth && form.values.DayOfWeekForMonthly;

			if (!hasDayOfMonth && !hasNthDay) {
				newErrors.push("For monthly recurrence, select either a specific day or a relative day (e.g., 2nd Tuesday)");
				form.setErrors((prev) => ({ ...prev, DayOfMonth: "Select a specific day of month", WeekOfMonth: "Select a specific week of month", DayOfWeekForMonthly: "Select a specific day of week" }));
			}

			if (form.values.DayOfMonth && (parseInt(form.values.DayOfMonth) < 1 || parseInt(form.values.DayOfMonth) > 31)) {
				newErrors.push("Day of month must be between 1 and 31");
			}
		}

		// if (newErrors.length > 0) {
		// 	setErrors(newErrors);
		// 	return;
		// }

		if (!form.isValid()) {
			return;
		}

		const recurrencePattern: RecurrencePatternDto = {
			WeekInterval: recurrenceType === RecurrenceType.Weekly ? parseInt(form.values.WeekInterval) || 1 : undefined,
			DaysOfWeek: recurrenceType === RecurrenceType.Weekly ? form.values.DaysOfWeek.map(d => parseInt(d)) : undefined,
			DayOfMonth: recurrenceType === RecurrenceType.Monthly && form.values.DayOfMonth ? parseInt(form.values.DayOfMonth) : undefined,
			WeekOfMonth: recurrenceType === RecurrenceType.Monthly && form.values.WeekOfMonth ? parseInt(form.values.WeekOfMonth) : undefined,
			DayOfWeekForMonthly: recurrenceType === RecurrenceType.Monthly && form.values.DayOfWeekForMonthly ? parseInt(form.values.DayOfWeekForMonthly) : undefined,
		};

		const submitData = schedule
			? ({
					ScheduleName: form.values.ScheduleName,
					Description: form.values.Description,
					RecurrenceType: parseInt(form.values.RecurrenceType),
					RecurrencePattern: recurrencePattern,
					ExcludedDaysOfWeek: recurrenceType === RecurrenceType.OneTime ? [] : form.values.ExcludedDaysOfWeek.map(d => parseInt(d)),
					EndDate: recurrenceType === RecurrenceType.OneTime ? undefined : form.values.EndDate || undefined,
					TimeOfExecution: localToUtcTime(form.values.TimeOfExecution),
					IsActive: form.values.IsActive,
			  } as UpdateWorkOrderScheduleDto)
			: ({
					ScheduleName: form.values.ScheduleName,
					Description: form.values.Description,
					WorkOrderTemplateId: form.values.WorkOrderTemplateId,
					RecurrenceType: parseInt(form.values.RecurrenceType),
					RecurrencePattern: recurrencePattern,
					ExcludedDaysOfWeek: recurrenceType === RecurrenceType.OneTime ? [] : form.values.ExcludedDaysOfWeek.map(d => parseInt(d)),
					StartDate: form.values.StartDate,
					EndDate: recurrenceType === RecurrenceType.OneTime ? undefined : form.values.EndDate || undefined,
					TimeOfExecution: localToUtcTime(form.values.TimeOfExecution),
					IsActive: form.values.IsActive,
			  } as CreateWorkOrderScheduleDto);

		// setErrors([]);

		onSubmit(submitData);
	};

	const templateOptions = (templates ?? []).map((t) => ({
		value: t.WorkOrderTemplateId,
		label: t.Title,
	}));

	if (schedule?.Deleted != null) {
		return (
			<Paper withBorder p="md" radius="md" style={{ height: isMobile ? 'calc(100vh)' : 'calc(100vh - 158px)' }}>
				<Stack gap="xs" mt="xl" justify="center" align="center">
					<ThemeIcon size={80} radius={80} color="red" variant="light">
						<IconTrash size={40} />
					</ThemeIcon>
					<Text fz="xl" fw="bold">
						Schedule Deleted
					</Text>
					<Text ta="center">This schedule has been deleted and cannot be edited.</Text>
				</Stack>
			</Paper>
		);
	}

	return (
		<Paper withBorder={!isMobile} p={0} radius={isMobile ? "0" : "md"} style={{ overflow: "hidden" }}>
			<ScrollArea type={isMobile ? "never" : "hover"} style={{ height: isMobile ? 'calc(100vh)' : 'calc(100vh - 160px)' }}>
				<Stack gap={0} mb="md" justify="center" className={classes['header']}>
					<Group justify="space-between" px="20" wrap="nowrap">
						<Group gap="xs" wrap="nowrap">
							<Button variant="subtle" radius="xl" ml={-15} onClick={onCancel} miw={100} leftSection={<IconChevronLeft size={20} />}>
								Back
							</Button>
							<Title order={isMobile ? 6 : 3}>{isNew ? "New Schedule" : "Edit Schedule"}</Title>
						</Group>
						<Button
							onClick={() => validateAndSubmit()}
							disabled={!form.isDirty()}
							loading={isSaving}
							variant="filled"
							radius="xl"
							miw={100}
							leftSection={<IconCheck />}
						>
							Save
						</Button>
					</Group>
				</Stack>
				<Container px="20">
					{((form.errors && Object.keys(form.errors).length > 0) || errors.length > 0) && <>
						<Alert title="Please fix the following errors:" color="red" mb="md" radius="lg">
							<Stack gap={0} mb={0}>
								{Object.entries(form.errors).map(([field, error]) => error && 
									<Group gap={5} key={field} wrap="nowrap" align="flex-start">
										<ThemeIcon c="red" w={20} variant="transparent" m={-5} mt={-3}><IconAlertTriangle size={14} /></ThemeIcon>
										<Text key={field} fz="sm">{error}</Text>
									</Group>
								)}
								{errors.map((error, index) => (
									<Group gap={5} key={index} wrap="nowrap" align="flex-start">
										<ThemeIcon c="red" w={20} variant="transparent" m={-5} mt={-3}><IconAlertTriangle size={14} /></ThemeIcon>
										<Text fz="sm">{error}</Text>
									</Group>
								))}
							</Stack>
						</Alert>
						<Divider mb="md" />
					</>}
					{/* {errors.length > 0 && (
						<>
							<Alert title="Please fix the following errors:" color="red" mb="md" radius="lg">
								<Stack gap={0} mb={0}>
									{errors.map((error, index) => (
										<Group gap={5} key={index} wrap="nowrap" align="flex-start">
											<ThemeIcon c="red" w={20} variant="transparent" m={-5}><IconAlertTriangle size={14} /></ThemeIcon>
											<Text fz="sm">{error}</Text>
										</Group>
									))}
								</Stack>
							</Alert>
							<Divider mb="md" />
						</>
					)} */}

					<form onSubmit={form.onSubmit(() => validateAndSubmit())}>
						<Button type="submit" style={{ display: 'none' }} />

						{/* Basic Info */}
						<TextInput
							label="Schedule Name"
							placeholder="Enter schedule name"
							key={form.key("ScheduleName")}
							{...form.getInputProps("ScheduleName")}
							required
							mb="xl"
						/>

						<Select
							label="Work Order Template"
							placeholder="Select a template"
							data={templateOptions}
							disabled={!!schedule}
							key={form.key("WorkOrderTemplateId")}
							{...form.getInputProps("WorkOrderTemplateId")}
							required
							mb="xl"
						/>

						<Textarea
							label="Description"
							placeholder="Enter description (optional)"
							key={form.key("Description")}
							{...form.getInputProps("Description")}
							autosize
							minRows={3}
							mb="xl"
						/>

						{/* Recurrence Configuration */}
						{/* <Text fw={500} mb="md">Recurrence Pattern</Text> */}
						<Divider label="Recurrence Pattern" mb="md" />

						<Select
							label="Recurrence Type"
							placeholder="Select recurrence type"
							data={[
								{ value: RecurrenceType.OneTime.toString(), label: "One Time" },
								{ value: RecurrenceType.Daily.toString(), label: "Daily" },
								{ value: RecurrenceType.Weekly.toString(), label: "Weekly" },
								{ value: RecurrenceType.Monthly.toString(), label: "Monthly" },
							]}
							key={form.key("RecurrenceType")}
							{...form.getInputProps("RecurrenceType")}
							mb="xl"
						/>

						{recurrenceType === RecurrenceType.Weekly && (
							<>
								<Select
									label="Every X Weeks"
									placeholder="Select interval"
									data={["1", "2", "3", "4"].map((n) => ({ value: n, label: `Every ${n} week(s)` }))}
									key={form.key("WeekInterval")}
									{...form.getInputProps("WeekInterval")}
									mb="xl"
								/>

								<MultiSelect
									label="Days of Week"
									placeholder="Select days"
									data={DaysOfWeek.map(d => ({ label: d.label, value: d.value.toString() }))}
									key={form.key("DaysOfWeek")}
									{...form.getInputProps("DaysOfWeek")}
									required
									mb="xl"
								/>
							</>
						)}

						{recurrenceType === RecurrenceType.Monthly && (
							<>
								<Stack gap="md" mb="xl">
									<Stack gap="xs">
										<Text size="sm" c="dimmed">Option 1: Specific Day of Month</Text>
										<NumberInput
											placeholder="Day (1-31)"
											// type="number"
											min={1}
											max={31}
											maw={250}
											key={form.key("DayOfMonth")}
											{...form.getInputProps("DayOfMonth")}
										/>
									</Stack>

									<Stack gap="xs">
										<Text size="sm" c="dimmed">Option 2: Relative Day (e.g., 2nd Tuesday)</Text>
										<Group grow>
											<Select
												label="Week"
												placeholder="Select week"
												data={MonthlyWeekOptions.map(m => ({ label: m.label, value: m.value.toString() }))}
												key={form.key("WeekOfMonth")}
												{...form.getInputProps("WeekOfMonth")}
											/>
											<Select
												label="Day of Week"
												placeholder="Select day"
												data={DaysOfWeek.map(d => ({ label: d.label, value: d.value.toString() }))}
												key={form.key("DayOfWeekForMonthly")}
												{...form.getInputProps("DayOfWeekForMonthly")}
											/>
										</Group>
									</Stack>
								</Stack>
							</>
						)}

						{/* Exclusions and Time */}
						{/* <Text fw={500} mb="md">Schedule Details</Text> */}

						{recurrenceType !== RecurrenceType.OneTime && (
							<MultiSelect
								label="Excluded Days of Week"
								placeholder="Select days to skip (optional)"
								data={DaysOfWeek.map(d => ({ label: d.label, value: d.value.toString() }))}
								key={form.key("ExcludedDaysOfWeek")}
								{...form.getInputProps("ExcludedDaysOfWeek")}
								mb="xl"
							/>
						)}

						<Divider label="Schedule Details" mb="md" />

						<SimpleGrid cols={isMobile ? 1 : 2}>
							<DatePickerInput
								label={recurrenceType === RecurrenceType.OneTime ? "Execution Date" : "Start Date"}
								placeholder="Select start date"
								valueFormat="MMM DD, YYYY"
								key={form.key("StartDate")}
								{...form.getInputProps("StartDate")}
								minDate={schedule != null ? moment.min(moment(), moment(schedule.StartDate)).toDate() : new Date()}
								// disabled={!!schedule && recurrenceType !== RecurrenceType.OneTime}
								required
							/>
							{recurrenceType !== RecurrenceType.OneTime && (
								<DatePickerInput
									label="End Date (Optional)"
									placeholder="No end date"
									valueFormat="MMM DD, YYYY"
									key={form.key("EndDate")}
									{...form.getInputProps("EndDate")}
									clearable
								/>
							)}
							<TimeInput
								label="Execution Time"
								{...form.getInputProps("TimeOfExecution")}
								withSeconds
								mb="xl"
							/>
						</SimpleGrid>

						<Checkbox label="Active" key={form.key("IsActive")} {...form.getInputProps("IsActive", { type: "checkbox" })} mb="xl" />
					</form>
				</Container>
			</ScrollArea>
		</Paper>
	);
};
